/**
 * Who may change a trip's plan — one rule for the server and the planner —
 * and the levels the planner's "People on this trip" sheet sets.
 *
 * Written as CommonJS so the frontend can `import` it and the backend can
 * `require` it, from one file (same pattern as `sortKeys.js`).
 * ⚠️ No object spread (`{ ...x }`) in this file: the frontend build's Babel
 * turns it into an ES-module `import` of a runtime helper, webpack then treats
 * the file as an ES module and ignores `module.exports`, and the production
 * build fails ("'levelPatchBody' is not exported", deploy 44c48c45,
 * 2026-10-17). `Object.assign` instead. Dev and Jest don't show it.
 *
 * D9 (docs/plans/TRIP_COEDITING_PLAN.md §10): everyone on the trip except a
 * traveller marked can't-edit. The owner and co-organizers always; any other
 * accepted traveller unless `can_edit` is 0. `can_edit` is DEFAULT-ALLOW — the
 * column is `BOOLEAN DEFAULT TRUE`, so an unset (NULL) value edits
 * (server/CLAUDE.md). The SQL form is `travelerCanEditSql` in
 * server/services/tripPermissionsHelper.js.
 */

const EDITOR_ROLES = ['co-organizer', 'co-owner'];
// The sheet's name for the same set.
const CO_ORGANIZER_ROLES = EDITOR_ROLES;

/**
 * An accepted traveller row ({ role, can_edit }) that may change the plan.
 * Membership (accepted, not removed) is the caller's to have checked.
 */
function travelerCanEdit(traveler) {
  if (!traveler) return false;
  if (EDITOR_ROLES.includes(traveler.role)) return true;
  return traveler.can_edit !== 0 && traveler.can_edit !== false;
}

/**
 * What the viewer may do on a trip, read from the trip GET's own copy
 * (`user_id` + the `travelers` roster). One reading for the planner's first
 * load and its live re-reads (useTripState), so a traveller whose rights
 * change mid-session lands where a fresh load would put them.
 *
 * Only the owner and accepted travellers edit — the server refuses anyone
 * else's save, and a pending invitee's copy can lack activities. Among them,
 * travelerCanEdit decides.
 *
 * @param {object|null} tripData the trip GET's trip
 * @param {string|null} userId the viewer
 * @returns {{ isOwner: boolean, isMember: boolean, role: string, canEdit: boolean }}
 */
function tripEditRights(tripData, userId) {
  const me = userId ? (tripData?.travelers || []).find(t => t.user_id === userId) : null;
  const isOwner = !!userId && tripData?.user_id === userId;
  const isMember = isOwner || me?.status === 'accepted';
  const canEdit = isMember && (isOwner || travelerCanEdit(me));
  return { isOwner, isMember, role: isOwner ? 'owner' : me?.role || 'participant', canEdit };
}

/**
 * The level the "People on this trip" sheet shows for a traveller:
 * 'organizer' (the trip's owner — not changeable), 'co-organizer', 'edit',
 * 'view', or null for someone the sheet offers no choice for (a child
 * profile, a former member). A row with no can_edit yet (someone just added,
 * before the roster re-reads) shows 'edit' — the column's default.
 */
function travellerLevel(traveller, ownerId) {
  if (!traveller) return null;
  const id = traveller.user_id || traveller.id;
  if (traveller.role === 'owner' || (ownerId && id === ownerId)) return 'organizer';
  if (traveller.traveler_type === 'child' || traveller.type === 'child' || traveller.former_member) return null;
  if (CO_ORGANIZER_ROLES.includes(traveller.role)) return 'co-organizer';
  return travelerCanEdit(traveller) ? 'edit' : 'view';
}

/**
 * May this viewer change `traveller`'s level? The owner: anyone with a level
 * but themselves; a co-organizer: plain travellers only (never another
 * co-organizer, never themselves) — the permissions route's own rules. A
 * declined invite has nothing to set. One test for the drawer's "Who can
 * edit" entry and the sheet's pickers, so the entry never opens a sheet with
 * nothing to change.
 */
function canChangeLevel(traveller, { ownerId, currentUserId, isTripOwner }) {
  const level = travellerLevel(traveller, ownerId);
  const id = traveller?.user_id || traveller?.id;
  return !!level && level !== 'organizer'
    && !(currentUserId && id === currentUserId)
    && traveller.status !== 'declined'
    && (!!isTripOwner || level !== 'co-organizer');
}

/**
 * The PATCH body that moves a traveller to `level`. Leaving co-organizer
 * says the new role and the edit right in one call, so there's no moment a
 * demoted co-organizer holds a stale right.
 */
function levelPatchBody(level) {
  if (level === 'co-organizer') return { role: 'co-organizer' };
  if (level === 'edit') return { role: 'participant', canEdit: true };
  if (level === 'view') return { role: 'participant', canEdit: false };
  return null;
}

/** The roster row as it stands after a move to `level` (optimistic copy). */
function withLevel(traveller, level) {
  if (level === 'co-organizer') return Object.assign({}, traveller, { role: 'co-organizer', can_edit: 1 });
  if (level === 'edit') return Object.assign({}, traveller, { role: 'participant', can_edit: 1 });
  if (level === 'view') return Object.assign({}, traveller, { role: 'participant', can_edit: 0 });
  return traveller;
}

module.exports = {
  travelerCanEdit,
  EDITOR_ROLES,
  CO_ORGANIZER_ROLES,
  tripEditRights,
  travellerLevel,
  canChangeLevel,
  levelPatchBody,
  withLevel
};
